// src/components/AffectedMembersPanel.js
//
// RULING 270 (2026-10-02): the box on the claim - "Members affected were notified and given an opportunity to be
// heard," with who, when and how. src\data\affectedMembers.js decides; this draws and writes. Read with M-3: this
// records the notice only; representation is M-3's and each General Chairperson's.
//
// Shown to everyone who may already see the claim. Recorded by an officer of the claim: the write is an arrayUnion
// onto the claim (affectedMembersNotice), so the claims update rule is the gate (lcEdit / gcEdit / adminEdit) - no
// UI permission gate beyond offering the form to officers, and a refusal is shown as it comes (ruling 70 pattern).
// Withdrawable by number: RULING_270_AFFECTED_NOTICE false and this renders nothing.
import React, { useMemo, useState } from 'react';
import { db } from '../firebase';
import { doc, updateDoc, serverTimestamp, arrayUnion } from 'firebase/firestore';
import { claimPatch } from '../data/claimWrite';
import {
  RULING_270_AFFECTED_NOTICE, AFFECTED_NOTICE_SENTENCE, AFFECTS, NOTICE_MEANS, noticeRecord, affectedNoticeView,
} from '../data/affectedMembers';
import RulingRef from './RulingRef';

const C = { card: '#0D1B2A', line: '#2E4A6B', dim: '#8AA4BF', accent: '#E8A020', bad: '#E06C6C', ok: '#5FB878', text: '#DCE7F2' };
const LABEL = { fontSize: 10, color: C.dim, textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 4 };
const today = () => new Date().toISOString().slice(0, 10);

export default function AffectedMembersPanel({ claim, claimId, user, onRecorded }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ affects: [] });
  const [errs, setErrs] = useState([]);
  const [saving, setSaving] = useState(false);
  const view = useMemo(() => affectedNoticeView(claim), [claim]);
  if (!RULING_270_AFFECTED_NOTICE || !claim || claim.status === 'draft') return null;
  const isOfficer = !!(user && (user.isAdmin || user.role === 'general_chair' || user.role === 'local_chair'));
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const toggle = (k) => setF(p => ({ ...p, affects: p.affects.includes(k) ? p.affects.filter(x => x !== k) : p.affects.concat([k]) }));
  const reset = () => { setOpen(false); setF({ affects: [] }); setErrs([]); };

  const record = async () => {
    const r = noticeRecord({ ...f, today: today() }, user);
    setErrs(r.errors);
    if (!r.ok) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'claims', claimId), claimPatch({ affectedMembersNotice: arrayUnion(r.record), updatedAt: serverTimestamp() }, user));
      if (onRecorded) onRecorded(r.record);
      reset();
    } catch (e) {
      // A refused write must never look like a recorded notice (PD-5).
      setErrs(['NOT recorded: ' + ((e && e.message) || 'the write was rejected') + '. Your entry is still here - nothing was saved.']);
    } finally { setSaving(false); }
  };

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-header">
        <div>
          <div className="card-title">Other members affected</div>
          <div className="card-subtitle">
            Where a claim would affect another member - seniority, a position, a vacation choice, pay - the record says
            whether they were notified and given an opportunity to be heard. Representation is a separate matter.
            <RulingRef user={user} n={270} why="Where a claim would affect other members, the record says whether they were notified and given an opportunity to be heard, with who, when and how." />
          </div>
        </div>
      </div>

      {view.otherEmployeeOnClaim ? (
        <div style={{ fontSize: 12, color: C.dim, marginBottom: 8 }}>The claim names another employee: {view.otherEmployeeOnClaim}.</div>
      ) : null}

      {view.recorded ? view.entries.map((e, i) => (
        <div key={i} style={{ background: C.card, border: '1px solid ' + C.line, borderRadius: 6, padding: '8px 12px', marginBottom: 8 }}>
          <div style={{ fontSize: 12, color: C.ok, fontWeight: 700 }}>{AFFECTED_NOTICE_SENTENCE}</div>
          <div style={{ fontSize: 12, color: C.text, marginTop: 2 }}>{e.line}</div>
          {e.note ? <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>{e.note}</div> : null}
        </div>
      )) : <div style={{ fontSize: 12, color: C.dim, marginBottom: 8 }}>{view.emptyText}</div>}

      {isOfficer && (!open ? (
        <button className="btn btn-secondary" style={{ fontSize: 11 }} onClick={() => { setOpen(true); setErrs([]); }}>+ Record a notice to affected members</button>
      ) : (
        <div style={{ background: C.card, border: '1px solid ' + C.line, borderRadius: 6, padding: '12px 14px' }}>
          <label style={LABEL}>Who was notified</label>
          <input aria-label="Who was notified" className="form-input" style={{ fontSize: 12, marginBottom: 8 }} value={f.members || ''} onChange={e => set('members', e.target.value)} placeholder="name(s), and roster number where known" />
          <label style={LABEL}>What the claim would affect for them</label>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12, color: C.text, marginBottom: 8 }}>
            {AFFECTS.map(a => <label key={a.key} style={{ cursor: 'pointer' }}><input aria-label="What the claim would affect for them" type="checkbox" checked={f.affects.includes(a.key)} onChange={() => toggle(a.key)} /> {a.label}</label>)}
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
            <div style={{ flex: '1 1 140px' }}>
              <label style={LABEL}>Date notified</label>
              <input aria-label="Date notified" className="form-input" type="date" style={{ fontSize: 12 }} value={f.notifiedOn || ''} onChange={e => set('notifiedOn', e.target.value)} />
            </div>
            <div style={{ flex: '1 1 180px' }}>
              <label style={LABEL}>How</label>
              <select aria-label="How" className="form-select" style={{ fontSize: 12 }} value={f.how || ''} onChange={e => set('how', e.target.value)}>
                <option value="">Choose…</option>
                {NOTICE_MEANS.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
              </select>
            </div>
            {f.how === 'other' && (
              <div style={{ flex: '1 1 180px' }}>
                <label style={LABEL}>Which way</label>
                <input aria-label="Which way" className="form-input" style={{ fontSize: 12 }} value={f.howOther || ''} onChange={e => set('howOther', e.target.value)} />
              </div>
            )}
          </div>
          <label style={{ fontSize: 12, color: C.text, cursor: 'pointer', display: 'block', marginBottom: 8 }}>
            <input aria-label="They were given an opportunity to be heard." type="checkbox" checked={f.heardOffered === true} onChange={e => set('heardOffered', e.target.checked)} /> They were given an opportunity to be heard.
          </label>
          <label style={LABEL}>Note (optional)</label>
          <input aria-label="Note (optional)" className="form-input" style={{ fontSize: 12, marginBottom: 8 }} value={f.note || ''} onChange={e => set('note', e.target.value)} />
          {errs.length > 0 && (
            <div style={{ border: '1px solid ' + C.bad, background: '#2A0F0F', color: '#F4A0A0', padding: '8px 12px', marginBottom: 8, fontSize: 12, lineHeight: 1.6 }}>
              {errs.map((x, i) => <div key={i}>{x}</div>)}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary" style={{ fontSize: 11 }} disabled={saving} onClick={reset}>Cancel</button>
            <button className="btn btn-secondary" style={{ fontSize: 11, color: C.ok, borderColor: C.ok }} disabled={saving} onClick={record}>{saving ? 'Recording…' : 'Record it'}</button>
          </div>
        </div>
      ))}
    </div>
  );
}
